/**
 * Analytics · Report Analytics
 * Demo content - see src/demo/analytics.ts.
 */

import React from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, Kpi, PageHead, Table, Tag, Bar, Note, DemoBadge } from '../../components/ui';
import { ChartFrame, Trend, Bars, STATUS } from '../../components/charts';
import { REPORT_ANALYTICS as RA } from '../../demo/analytics';

export const ReportAnalyticsPage: React.FC = () => (
  <AppShell
    title="Report Analytics"
    sub="Analytics"
    actions={
      <>
        <DemoBadge />
        <button className="btn btn-g">Last 8 weeks</button>
      </>
    }
  >
    <PageHead
      eyebrow="Analytics"
      title="Are the reports being read — and acted on"
      lede="A report nobody opens is a report nobody needed. This tracks delivery, opens and the sections that actually led to an action."
    />

    <div className="g g4" style={{ marginBottom: 12 }}>
      {RA.kpis.map((k) => (
        <Kpi
          key={k.label}
          label={k.label}
          value={k.value}
          unit={'unit' in k ? (k as any).unit : undefined}
          color={k.color}
          icon={k.icon as any}
          pip={'pip' in k ? (k as any).pip : undefined}
          dir={('dir' in k ? (k as any).dir : '') as any}
          sub={'sub' in k ? (k as any).sub : undefined}
        />
      ))}
    </div>

    <div className="g g2" style={{ marginBottom: 12 }}>
      <ChartFrame title="Generated against opened" sub="per week">
        <Trend
          data={RA.delivery}
          x="week"
          series={[
            { key: 'generated', label: 'Generated' },
            { key: 'opened', label: 'Opened' },
          ]}
        />
      </ChartFrame>

      <ChartFrame title="Confidence spread" sub="reports by confidence band">
        <Bars data={RA.confidenceSpread} x="band" horizontal series={[{ key: 'reports', label: 'Reports' }]} />
      </ChartFrame>
    </div>

    <div className="g g3">
      <div className="s2">
        <Card title="Who reads them" sub="opens and actions taken, last 44 reports" flush>
          <Table
            cols={[
              { t: 'Reader' },
              { t: 'Role' },
              { t: 'Opened', num: true },
              { t: 'Open rate' },
              { t: 'Actions taken', num: true },
            ]}
            rows={RA.readers.map((r) => {
              const rate = Math.round((r.opened / r.of) * 100);
              return [
                <b>{r.name}</b>,
                <span className="dim">{r.role}</span>,
                `${r.opened} / ${r.of}`,
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Bar
                    pct={rate}
                    width={60}
                    color={rate >= 80 ? STATUS.good : rate >= 50 ? STATUS.warning : STATUS.serious}
                  />
                  <span className="mono">{rate}%</span>
                </span>,
                r.acted,
              ];
            })}
          />
        </Card>
      </div>

      <Card title="Which sections get acted on" sub="reads vs actions">
        {RA.sections.map((s) => (
          <div className="split" key={s.section}>
            <span>{s.section}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="dim" style={{ fontSize: 11 }}>
                {s.reads}% read
              </span>
              <Tag tone={s.tone as any}>{s.actions}% acted</Tag>
            </span>
          </div>
        ))}
        <Note style={{ marginTop: 10 }}>
          <b>"For information" earns its name.</b> 38% read, 3% acted — worth cutting to shorten the
          report rather than keeping for completeness.
        </Note>
      </Card>
    </div>
  </AppShell>
);
